/**
 * @fileoverview O aviso quando a Empresa chega a um limite do plano (31/10/2026,
 * etapa 7 dos planos), no lugar do toast do erro: um modal curto que diz o que
 * chegou ao limite (quanto usa de quanto). Para quem gere a cobrança, no
 * Starter, abre a contratação ali mesmo (o Basic ou o Pro; no limite do Chat
 * Interno, o Chat Interno); quem já assina (etapa 8) abre a alteração já com o
 * que resolve (mais uma licença, mais um Chat Interno, o Pro para contas,
 * números e quadros); nos outros casos, orienta a falar com o suporte.
 * Quem não gere a cobrança é orientado a falar com o administrador. Montado
 * uma vez no layout; o toastError dispara o evento (utils/planLimitEvent).
 */

import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { Dialog, IconButton } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { X } from "lucide-react";

import PlansDialog from "../PlansDialog";
import { useBilling } from "../../../context/Billing/BillingContext";
import api from "../../../services/api";
import { i18n } from "../../../translate/i18n";
import { money } from "../../../utils/billingPlans";
import { PLAN_LIMIT_EVENT } from "../../../utils/planLimitEvent";
import { dialogShell } from "../../../styles/dialogShell";

const t = (key, options) => i18n.t(`planLimit.${key}`, options);

const RESOURCE_OF = {
  ERR_PLAN_LIMIT_USERS: "users",
  ERR_PLAN_LIMIT_INTERNAL_CHAT: "internalChat",
  ERR_PLAN_LIMIT_ACCOUNTS: "accounts",
  ERR_PLAN_LIMIT_WHATSAPPS: "whatsapps",
  ERR_PLAN_LIMIT_CRM_BOARDS: "crmBoards",
};

const useStyles = makeStyles((theme) => {
  const dark = theme.palette.type === "dark";
  const text = dark ? "#FAFAFA" : "#09090B";
  const muted = dark ? "#A1A1AA" : "#52525B";
  const line = dark ? "rgba(255,255,255,0.08)" : "#E4E4E7";
  const focus = `0 0 0 1px ${dark ? "rgba(255,255,255,0.7)" : "rgba(9,9,11,0.7)"}`;
  return {
    ...dialogShell(theme, { width: 460 }),
    body: { padding: "16px 22px 6px", display: "flex", flexDirection: "column", gap: 10 },
    lead: { margin: 0, fontSize: 14.5, lineHeight: 1.55, color: text },
    text: { margin: 0, fontSize: 13.5, lineHeight: 1.55, color: muted },
    primary: {
      height: 40,
      borderRadius: 9,
      border: "none",
      background: "#3D5CFD",
      color: "#fff",
      fontFamily: "inherit",
      fontSize: 13.5,
      fontWeight: 700,
      padding: "0 18px",
      cursor: "pointer",
      "&:disabled": { opacity: 0.55, cursor: "default" },
      "&:focus-visible": { outline: "none", boxShadow: `0 0 0 1px ${dark ? "#fff" : "#09090B"}` },
    },
    secondary: {
      height: 40,
      borderRadius: 9,
      border: `1px solid ${line}`,
      background: "transparent",
      color: text,
      fontFamily: "inherit",
      fontSize: 13.5,
      fontWeight: 600,
      padding: "0 16px",
      cursor: "pointer",
      "&:focus-visible": { outline: "none", boxShadow: focus },
    },
  };
});

const PlanLimitPrompt = ({ managesBilling }) => {
  const classes = useStyles();
  const history = useHistory();
  const { refetchBilling } = useBilling();
  const [limit, setLimit] = useState(null);
  const [overview, setOverview] = useState(null);
  const [checked, setChecked] = useState(false);
  const [plansMode, setPlansMode] = useState(null);

  useEffect(() => {
    const onLimit = (event) => {
      event.preventDefault();
      setOverview(null);
      setChecked(false);
      setLimit(event.detail);
    };
    window.addEventListener(PLAN_LIMIT_EVENT, onLimit);
    return () => window.removeEventListener(PLAN_LIMIT_EVENT, onLimit);
  }, []);

  // Quem gere a cobrança: o retrato da cobrança diz se dá para contratar aqui (Starter, sem pedido aberto).
  useEffect(() => {
    if (!limit || !managesBilling) return undefined;
    let alive = true;
    api
      .get("/billing/overview")
      .then(({ data }) => alive && setOverview(data))
      .catch(() => alive && setOverview(null))
      .finally(() => alive && setChecked(true));
    return () => {
      alive = false;
    };
  }, [limit, managesBilling]);

  const close = () => {
    setLimit(null);
    setPlansMode(null);
  };

  if (!limit) return null;

  const resource = RESOURCE_OF[limit.code];
  const lead = resource && typeof limit.used === "number" && typeof limit.limit === "number" ? t(`used.${resource}`, { used: limit.used, limit: limit.limit }) : t("generic");
  const ready = managesBilling && overview && !overview.legacy;
  // A contratação parte do Starter sem assinatura.
  const canContract = ready && overview.plan === "starter" && overview.enabled && !overview.courtesy && !overview.pendingRequest && !overview.subscription;
  const pending = ready && !!overview.pendingRequest;
  // A mensalidade não paga vem antes de qualquer alteração (o servidor também recusa).
  const paymentPending = ready && !overview.pendingRequest && overview.subscription?.status === "failed";
  // Com uma redução agendada, o limite já é o do plano menor e o aumento fica travado: desfazer em Faturamento.
  const reductionScheduled = ready && !overview.pendingRequest && !paymentPending && !!overview.scheduledReduction;
  // Quem já assina (etapa 8): aumentar, pagando só a diferença até a renovação. No Starter, a alteração só aumenta o
  // Chat Interno (até o máximo de pessoas dele): os outros limites dele são do suporte.
  const starterPlan = ready ? overview.catalog.plans.find((plan) => plan.key === "starter") : null;
  const internalRoom = ready && (overview.terms?.internalChatSeats || 0) < (starterPlan?.maxUsers || 0);
  const canChange =
    ready &&
    overview.enabled &&
    !overview.courtesy &&
    !overview.pendingRequest &&
    !paymentPending &&
    !reductionScheduled &&
    !!overview.subscription &&
    (overview.plan !== "starter" || (resource === "internalChat" && internalRoom));
  const internalOffer = canContract && resource === "internalChat";
  // No Basic, contas, números e quadros só crescem no Pro.
  const proOffer = canChange && overview.plan === "basic" && ["accounts", "whatsapps", "crmBoards"].includes(resource);
  // Enquanto o retrato não chega, só o que chegou ao limite (sem piscar outra oferta).
  const waiting = managesBilling && !checked;
  const offer = waiting
    ? ""
    : canContract
      ? internalOffer
        ? t("internalOffer", { price: money(overview.catalog.internalChatPriceCents) })
        : t("starterOffer")
      : canChange
        ? proOffer
          ? t("changeOfferPro")
          : t("changeOffer")
        : pending
          ? t("pendingRequest")
          : paymentPending
            ? t("paymentPending")
            : reductionScheduled
              ? overview.scheduledReduction.kind === "cancellation"
                ? t("cancellationScheduled")
                : t("reductionScheduled")
              : managesBilling
              ? t("otherPlan")
              : t("askAdmin");

  if (plansMode) {
    return (
      <PlansDialog
        open
        mode={plansMode}
        overview={overview}
        focusResource={resource}
        onClose={close}
        onDone={() => {
          close();
          refetchBilling();
        }}
      />
    );
  }

  return (
    <Dialog open onClose={close} maxWidth={false} classes={{ paper: classes.dialogPaper }} aria-labelledby="plan-limit-title">
      <div className={classes.dialogHead}>
        <h2 className={classes.dialogTitle} id="plan-limit-title">
          {t("title")}
        </h2>
        <IconButton size="small" className={classes.dialogClose} onClick={close} aria-label={t("notNow")}>
          <X size={18} />
        </IconButton>
      </div>
      <div className={classes.body}>
        <p className={classes.lead}>{lead}</p>
        {offer && <p className={classes.text}>{offer}</p>}
      </div>
      <div className={classes.dialogFoot}>
        {waiting || canContract ? (
          <>
            <button type="button" className={classes.secondary} onClick={close}>
              {t("notNow")}
            </button>
            <button type="button" className={classes.primary} onClick={() => setPlansMode(internalOffer ? "internal" : "upgrade")} disabled={waiting}>
              {internalOffer ? t("addInternal") : t("seeOptions")}
            </button>
          </>
        ) : canChange ? (
          <>
            <button type="button" className={classes.secondary} onClick={close}>
              {t("notNow")}
            </button>
            <button type="button" className={classes.primary} onClick={() => setPlansMode("change")}>
              {t("seeOptions")}
            </button>
          </>
        ) : pending || paymentPending || reductionScheduled ? (
          <>
            <button type="button" className={classes.secondary} onClick={close}>
              {t("notNow")}
            </button>
            <button
              type="button"
              className={classes.primary}
              onClick={() => {
                close();
                history.push("/settings/billing");
              }}
            >
              {t("openBilling")}
            </button>
          </>
        ) : (
          <button type="button" className={classes.primary} onClick={close}>
            {t("ok")}
          </button>
        )}
      </div>
    </Dialog>
  );
};

export default PlanLimitPrompt;
